import { useState } from 'react';
import { Common } from '@thecvlb/design-system/lib/src';

import { useLazyCheckMedicationCoverageQuery } from 'services/patients/patients';

import { selectOrchestrate, selectSignUp } from 'store';
import { OrchestrateState } from 'store/orchestrate-signup/orchestrate.types';

import Loader from 'shared/Loader';
import RadioGroup from 'shared/RadioGroup';

import { SEMAGLUTIDE_PRICE_POINT } from 'constants/pricepoints';
import { useAppSelector } from 'hooks';
import { useGetSelectedItem } from 'hooks/useGetSelectedItem';
import useSubmitOrchestrateForm from 'hooks/useSubmitOrchestrateForm';
import { FlowTypes, PlanCodes } from 'utils/enums';

import Heading from '../../components/Heading';
import InsuranceMedicationsList from '../parts/InsuranceMedicationsList';

import { Option } from 'models/forms.types';
import { Medication } from 'models/medication.types';

type Props = { onContinue: () => void; selectedFlow: FlowTypes };

const ITEMS = [
  {
    label: 'Yes, let’s use my insurance',
    subtitle: 'Average time for insurance approval is 30 days',
    value: '1'
  },
  {
    label: 'No, I prefer not using insurance',
    subtitle: 'Get your medication delivered in less than 5 days',
    value: '2'
  },
  {
    label: 'I know my insurance will not cover GLP-1 medications',
    value: '3'
  },
  {
    label: 'Show me all options',
    value: '4'
  }
];

const getOptions = (
  options: (Option & {
    subtitle?: string;
  })[]
) => {
  const Option = ({ label, subtitle }: { label: string; subtitle?: string }) => (
    <div className="flex flex-col gap-0.5">
      <span className="">{label}</span>
      {!!subtitle && <span className="text-mSm text-gray md:text-sm">{subtitle}</span>}
    </div>
  );

  return options.map((option) => ({
    label: <Option key={option.value} {...option} />,
    value: option.value
  }));
};

const ShouldUseInsurance: React.FC<Props> = ({ onContinue, selectedFlow }) => {
  const { membershipPlans } = useAppSelector(selectSignUp);
  const {
    user: { accessToken }
  } = useAppSelector(selectOrchestrate);

  const WMPLan = membershipPlans.find((p) => p.planCode === PlanCodes.WeightManagementMembership);
  const WMPricePoints = WMPLan?.pricePoints;

  const semaglutidePP = WMPricePoints?.find((p) => p.planPricePointId === SEMAGLUTIDE_PRICE_POINT);

  const { send, isLoading } = useSubmitOrchestrateForm();
  const [checkMedications] = useLazyCheckMedicationCoverageQuery();
  const { value, setValue } = useGetSelectedItem('should_i_use_insurance');

  const [step, setStep] = useState<'initial' | 'processing' | 'results' | ''>('initial');
  const [medications, setMedications] = useState<Medication[]>([]);
  const [medicationCheckStatus, setMedicationCheckStatus] = useState('');

  const handleSelect = (v: string) => {
    if (v) {
      const answers: OrchestrateState['mif_qa'] = [
        { answer: v, question: 'should_i_use_insurance' }
      ];
      if (['2', '3'].includes(v)) {
        answers.push({
          answer: {
            copay: '',
            drug_name: 'Semaglutide',
            oldPrice: `$${semaglutidePP?.monthlyPrice}`
          },
          question: 'selected_medication'
        });
      }
      setValue(v);
      send('mif_qa', answers, () => {
        setValue('');
        if (
          selectedFlow === FlowTypes.WeightManagementFlowInsuranceMedications &&
          ['1', '4'].includes(v)
        ) {
          setStep('processing');
          const checkDelay: Promise<boolean> = new Promise((resolve) => {
            setTimeout(() => {
              resolve(true);
            }, 3000);
          });
          const promises = [checkDelay, checkMedications(accessToken || '').unwrap()];
          Promise.race(promises)
            .then((r) => {
              if (typeof r === 'boolean') {
                // logic for pending answer
                setMedicationCheckStatus('pending');
              } else {
                if (!r.data.length) {
                  setMedicationCheckStatus('error');
                } else {
                }
                setMedications(r.data);
              }
              setStep('results');
            })
            .catch(() => {
              setMedicationCheckStatus('error');
              setStep('results');
            });
        } else {
          onContinue();
        }
      });
    }
  };

  const mappedMedications: Medication[] = [
    ...(medications ?? [])?.map((m) => ({
      ...m,
      price: String(m.copay)
    })),
    ...(semaglutidePP
      ? [
          {
            copay: `${Number(semaglutidePP.monthlyPrice) - 100}/mo`,
            drug_name: 'Semaglutide',
            oldPrice: `$${semaglutidePP.monthlyPrice}`
          }
        ]
      : [])
  ];

  const headingContent = {
    initial: {
      subtitle: '',
      title: 'Do you want to use your insurance provider to pay for your medication?'
    },
    processing: {
      subtitle: `Hang tight! We're currently checking your insurance benefits to see how they align with our services. This process may take a few moments, but we're working swiftly to bring you closer to personalized care.`,
      title: 'Checking insurance'
    },
    results: {
      subtitle:
        medicationCheckStatus === 'error' ? (
          ''
        ) : medicationCheckStatus === 'pending' ? (
          `It’s taking longer than usual to determine your medication benefits. Please select an option to continue.`
        ) : mappedMedications.some((m) => 'status' in m && m.status === 'Covered') ? (
          <span>
            <b>Great news!</b> Your insurance covers branded GLP-1 medications at a great price.
            Please select an option to continue.
          </span>
        ) : mappedMedications.some((m) => 'status' in m && m.status === 'PA Required') ? (
          <span>
            <b>Your insurance requires another process to cover a GLP-1.</b> It’s common for
            insurance to require this authorization. Please select an option to continue.
          </span>
        ) : (
          <span>
            <b>Your insurance doesn’t cover GLP-1s, but you still have great options!</b> Don’t
            worry, we have great options for you such as compounded GLP-1 medication. Please select
            an option to continue.
          </span>
        ),
      title: medicationCheckStatus === 'pending' ? 'Results pending...' : 'Select your medication'
    }
  };

  const handleContinue = (v: string) => {
    const selectedMedication: OrchestrateState['mif_qa'][number]['answer'] | undefined =
      v === 'TBD'
        ? {
            drug_name: 'TBD'
          }
        : mappedMedications.find((m) => m.drug_name === v);
    if (!selectedMedication) {
      throw new Error('Selected medication not found');
    }
    send('mif_qa', [{ answer: selectedMedication, question: 'selected_medication' }], () => {
      onContinue();
    });
  };

  const content = {
    initial: (
      <RadioGroup
        dataTestId="toggle"
        isLoading={isLoading}
        items={getOptions(ITEMS)}
        selectedItem={value as string | undefined}
        onSelect={handleSelect}
      />
    ),
    processing: <Loader isVisible />,
    results: (
      <>
        {medicationCheckStatus === 'error' && (
          <>
            <Common.Alert dataTestId="cannot_determine_coverage" type="info" colorableBackground>
              We couldn’t determine your prescription coverage from your insurance provider.
            </Common.Alert>
            <p>
              <b>Don’t worry!</b> We still have a great cash-pay option (no insurance necessary):
            </p>
          </>
        )}
        {medicationCheckStatus === 'pending' && (
          <div className="flex flex-col gap-3">
            <button
              className="w-full rounded-2xl border bg-white px-6 py-4"
              onClick={() => handleContinue('TBD')}
            >
              <Common.RadioButton color="blue" dataTestId="checkbox">
                <div className="ml-1 flex flex-col items-start gap-0.5">
                  <span className="text-left" data-testid="decide_later">
                    Decide medication later...
                  </span>
                  <span className="text-left text-mSm text-gray md:text-sm">
                    We’ll work with your insurance provider to determine your best covered option.
                  </span>
                </div>
              </Common.RadioButton>
            </button>
            <button
              className="w-full rounded-2xl border bg-white px-6 py-4"
              onClick={() => handleContinue('Semaglutide')}
            >
              <Common.RadioButton color="blue" dataTestId="checkbox">
                <div className="ml-1 flex flex-col items-start gap-0.5">
                  <span className="text-left" data-testid="choose_cash">
                    Choose cash-pay option
                  </span>
                  <span className="text-left text-mSm text-gray md:text-sm">
                    Semaglutide: same active ingredient as Wegovy.
                  </span>
                </div>
              </Common.RadioButton>
            </button>
          </div>
        )}
        <InsuranceMedicationsList
          loading={isLoading}
          medications={mappedMedications}
          status={medicationCheckStatus}
          onSelect={handleContinue}
        />
      </>
    )
  };

  return (
    <>
      {!!step && (
        <>
          <div className="flex flex-col gap-6">
            <Heading
              category="Plan"
              subtitle={headingContent[step]?.subtitle}
              title={headingContent[step].title}
            />
            {content[step]}
          </div>
        </>
      )}
    </>
  );
};

export default ShouldUseInsurance;
